import AXIOS_INSTANCE from './axiosInstance'

const jsonApiHeaders = { 'Content-Type': 'application/vnd.api+json' }

export type ImmunisationRecordStatus = 'given' | 'given_elsewhere' | 'refused' | 'contraindicated'

export type ImmunisationForecastStatus =
  | ImmunisationRecordStatus
  | 'due'
  | 'overdue'
  | 'upcoming'
  | 'not_yet'
  | 'missed'

export type ImmunisationRecord = {
  id: string
  patient_id?: string | number | null
  vaccine_code: string
  vaccine_name?: string | null
  dose_number: number
  status: ImmunisationRecordStatus
  given_on: string | null
  batch_number?: string | null
  site?: string | null
  route?: string | null
  encounter_id?: string | number | null
  given_by_name?: string | null
  notes?: string | null
  created_at?: string
  updated_at?: string
}

export type ImmunisationForecastItem = {
  vaccine_code: string
  label: string
  dose_number: number
  group_label: string
  status: ImmunisationForecastStatus
  due_on: string | null
  record_id: string | number | null
}

export type ImmunisationSummary = { overdue: number; due: number; upcoming: number }

export type ImmunisationsResponse = {
  records: ImmunisationRecord[]
  forecast: ImmunisationForecastItem[]
  summary: ImmunisationSummary
}

export type Vaccine = {
  code: string
  name: string
  short_name?: string | null
  route?: string | null
  site_hint?: string | null
  active?: boolean
  source?: string | null
}

export type ImmunisationRecordAttributes = {
  patient_id: string
  vaccine_code: string
  dose_number: number
  status: ImmunisationRecordStatus
  given_on: string | null
  batch_number?: string | null
  site?: string | null
  route?: string | null
  encounter_id?: string | null
  notes?: string | null
}

export type ImmunisationRecordPayload = {
  data: { type: 'immunisation_record'; attributes: ImmunisationRecordAttributes }
}

type MaybeJsonApi = { id?: string | number; attributes?: Record<string, unknown> } & Record<string, unknown>

/** Records may come back flat or as JSON:API resources — always hand the UI a flat record with a string id. */
export const normaliseImmunisationRecord = (raw: MaybeJsonApi): ImmunisationRecord => {
  const flat = raw?.attributes ? { ...raw.attributes, id: raw.id } : raw
  return {
    ...(flat as unknown as ImmunisationRecord),
    dose_number: Number((flat as Record<string, unknown>).dose_number ?? 0),
    id: String(flat.id ?? '')
  }
}

const listOf = (value: unknown): MaybeJsonApi[] => {
  if (Array.isArray(value)) return value as MaybeJsonApi[]
  if (value && typeof value === 'object' && Array.isArray((value as { data?: unknown }).data)) {
    return (value as { data: MaybeJsonApi[] }).data
  }
  return []
}

/** GET /api/patients/:id/immunisations — the lifelong record plus the schedule forecast. */
export const fetchImmunisations = async (patientId: string): Promise<ImmunisationsResponse> => {
  const { data } = await AXIOS_INSTANCE.get(`patients/${patientId}/immunisations`)
  return {
    forecast: Array.isArray(data?.forecast) ? data.forecast : [],
    records: listOf(data?.records).map(normaliseImmunisationRecord),
    summary: { due: 0, overdue: 0, upcoming: 0, ...(data?.summary ?? {}) }
  }
}

/** GET /api/vaccines — the catalogue (code, name, route, ...). Accepts a flat list or JSON:API. */
export const fetchVaccines = async (): Promise<Vaccine[]> => {
  const { data } = await AXIOS_INSTANCE.get('vaccines')
  const list = Array.isArray(data?.vaccines) ? data.vaccines : listOf(data)
  return list.map((raw: MaybeJsonApi) => (raw?.attributes ? { ...raw.attributes } : raw) as Vaccine)
}

export const createImmunisationRecord = async (payload: ImmunisationRecordPayload) => {
  const { data } = await AXIOS_INSTANCE.post('immunisation_records', payload, { headers: jsonApiHeaders })
  return data
}

export const updateImmunisationRecord = async (id: string, payload: ImmunisationRecordPayload) => {
  const { data } = await AXIOS_INSTANCE.patch(`immunisation_records/${id}`, payload, { headers: jsonApiHeaders })
  return data
}

export const deleteImmunisationRecord = async (id: string) => {
  await AXIOS_INSTANCE.delete(`immunisation_records/${id}`)
}
